<template>
  <div v-if="isLISA3">
    <Message v-if="options.length === 0">
      <slot name="no-options">{{ t("global.na") }}</slot>
    </Message>
    <LISAButtonGroup
      v-if="options.length <= 3 && !forceSelect"
      v-model="lisaModelValue"
      :items="lisaSelectOptions"
      :orientation="orientation"
      full-width
      class="lisaui:w-full"
    >
      <template v-if="'append' in $slots" #append>
        <slot name="append" />
      </template>
    </LISAButtonGroup>
    <LISASelect
      v-else
      v-model="lisaModelValue"
      :options="lisaSelectOptions"
      v-bind="$attrs"
      :valid="valid"
      :invalid="invalid"
      :disabled="disabled"
    >
      <template v-if="'append' in $slots" #append>
        <slot name="append" />
      </template>
    </LISASelect>
  </div>
  <div v-else>
    <div
      v-if="options.length <= 3 && !forceSelect"
      :class="{ 'flex-column': !horizontal, 'p-invalid': invalid }"
      class="flex justify-content-between p-inputwrapper-filled"
    >
      <LISAButton
        v-for="(option, index) in options"
        :key="`button${index}`"
        :data-testid="`${dataTestid}-${option[optionValue]}`"
        :disabled="option.disabled || props.disabled"
        :style="{
          'border-top-left-radius': index === 0 && options.length !== 1 ? undefined : '0',
          'border-top-right-radius':
            ((index === 0 && !horizontal) || (index === options.length - 1 && horizontal)) &&
            options.length !== 1
              ? undefined
              : '0',
          'border-left-width': index === 0 || !horizontal ? undefined : '0',
          'border-top-width': index === 0 || horizontal ? undefined : '0',
          'border-bottom-left-radius':
            ((index === options.length - 1 && !horizontal) || (index === 0 && horizontal)) &&
            options.length !== 1
              ? undefined
              : '0',
          'border-bottom-right-radius':
            index === options.length - 1 && options.length !== 1 ? undefined : '0',
        }"
        :class="{
          'p-button-outlined': modelValue !== option[optionValue],
          'p-invalid': invalid,
        }"
        class="select-list-button w-100"
        @click="modelValue = option[optionValue]"
      >
        <i v-if="option.icon" :class="option.icon" />
        {{ option[optionLabel] }}
        <InfoTooltip
          v-if="option.disabled && option.disabledTooltip"
          :text="option.disabledTooltip"
        />
      </LISAButton>
      <Message v-if="options.length === 0" severity="info" class="w-100">
        <slot name="no-options">{{ t("global.na") }}</slot>
      </Message>
    </div>
    <Select
      v-else
      v-bind="$attrs"
      v-model="modelValue"
      :options="options"
      :data-testid="dataTestid"
      :option-value="optionValue"
      option-disabled="disabled"
      :option-label="optionLabel"
      :disabled="props.disabled"
      :invalid="invalid"
      :show-clear="clearable"
      class="lisa-input-select-list"
    >
      <template #option="{ option }">
        <i v-if="option.icon" :class="option.icon" class="mr-1" />
        <span class="select-list-option-label">{{ option[optionLabel] }}</span>
        <InfoTooltip
          v-if="option.disabled && option.disabledTooltip"
          :text="option.disabledTooltip"
          class="ml-2"
        />
      </template>
    </Select>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="
    T extends string,
    S extends string,
    U extends Record<T | S, any> & { disabled?: boolean; disabledTooltip?: string; icon?: string }
  "
>
import { computed, watch } from "vue";
import { isEqual } from "lodash";
import {
  LISAButtonGroup,
  LISASelect,
  type LISAButtonGroupOrientation,
} from "@fi-solutions/lisa-ui";
import { useTranslation } from "@/language/translation";
import { isLISA3 } from "@/composables/useLISA3";
import Select from "primevue/select";

const { t } = useTranslation();

const props = withDefaults(
  defineProps<{
    options: U[];
    optionValue?: T;
    optionLabel?: S;
    preselect?: boolean;
    horizontal?: boolean;
    forceSelect?: boolean;
    dataTestid?: string;
    disabled?: boolean;
    clearable?: boolean;
    invalid?: boolean;
    valid?: boolean;
    state?: boolean;
    orientation?: LISAButtonGroupOrientation;
  }>(),
  {
    optionValue: undefined,
    optionLabel: undefined,
    dataTestid: undefined,
    invalid: undefined,
    valid: undefined,
    state: undefined,
    orientation: "vertical",
  },
);

const modelValue = defineModel<U[T] | undefined>();

const optionValue = computed(() => props.optionValue ?? ("value" as T));
const optionLabel = computed(() => props.optionLabel ?? ("label" as S));

const lisaModelValue = computed<number | undefined>({
  get() {
    if (modelValue.value == null) return undefined;
    const index = props.options.findIndex((option) =>
      isEqual(option[optionValue.value], modelValue.value),
    );
    return index === -1 ? undefined : index;
  },
  set(index) {
    if (index == null) {
      modelValue.value = undefined;
      return;
    }
    const option = props.options[index];
    if (option) modelValue.value = option[optionValue.value];
  },
});

function setDefaultValue() {
  if (props.preselect && props.options.length > 0 && modelValue.value == null) {
    modelValue.value = props.options[0][optionValue.value];
  }
}

watch(() => props.options, setDefaultValue, { immediate: true });
watch(() => props.optionValue, setDefaultValue, { immediate: true });
watch(() => props.optionLabel, setDefaultValue, { immediate: true });

const lisaSelectOptions = computed(() => {
  return props.options.map((o, index) => {
    return {
      id: index,
      label: o[optionLabel.value],
      value: index,
      disabled: o.disabled,
      icon: o.icon,
      tooltip: o.disabledTooltip,
    };
  });
});
</script>

<style lang="scss" scoped>
.lisa-input-select-list {
  width: 100%;
  min-width: 0;
  max-width: 100%;

  :deep(.p-select-label) {
    flex: 1 1 0;
    width: 0;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.select-list-option-label {
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
}

.select-list-button {
  height: 2.28125rem;
  min-height: 2.28125rem;
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
  overflow: hidden;

  :deep(.p-button-label) {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
